"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useInfiniteCarousel = useInfiniteCarousel;
var _react = require("react");
var _base = require("@site/pattern-library-utils/base.functions");
/**
 * Custom hook to create an infinite carousel
 *
 * @param {Array} items List of items to display in the carousel
 * @param {number} clonedItemsCount Number of items to clone at the start and end of the list for infinite scrolling
 * @returns {Object} Carousel state and handlers
 * @returns {React.RefObject} containerRef Reference to the carousel container element
 * @returns {Array} extendedItems List of items including the cloned items for infinite scrolling
 * @returns {number} activeIndex Index of the currently active item
 * @returns {number} activeExtendedIndex Index of the currently active item in the extended list
 * @returns {Function} handleNext Function to scroll to the next item
 * @returns {Function} handlePrev Function to scroll to the previous item
 */

function useInfiniteCarousel(items, clonedItemsCount = 2) {
  const containerRef = (0, _react.useRef)(null);
  const [activeIndex, setActiveIndex] = (0, _react.useState)(0);
  const [activeExtendedIndex, setActiveExtendedIndex] = (0, _react.useState)(clonedItemsCount); // Centered Card Index
  const isScrollingRef = (0, _react.useRef)(false);
  const itemWidthRef = (0, _react.useRef)(0);
  const isRTL = (0, _base.useIsRTL)();
  const extendedItems = (0, _react.useMemo)(() => {
    if (!items || items.length === 0) return [];
    return [...items.slice(-clonedItemsCount), ...items, ...items.slice(0, clonedItemsCount)];
  }, [items, clonedItemsCount]);
  const updateItemWidth = (0, _react.useCallback)(() => {
    const container = containerRef.current;
    if (container && container.children.length > 0) {
      const firstItem = container.children[0];
      itemWidthRef.current = firstItem.offsetWidth || 0;
    } else {
      itemWidthRef.current = 0;
    }
  }, []);
  const resetPosition = (0, _react.useCallback)(() => {
    const container = containerRef.current;
    const itemWidth = itemWidthRef.current;
    if (!container || !itemWidth || typeof itemWidth !== 'number' || isNaN(itemWidth)) {
      return;
    }
    const scrollOffset = isRTL ? -container.scrollLeft : container.scrollLeft;
    const currentIndex = Math.round(scrollOffset / itemWidth);
    const nearStart = scrollOffset < itemWidth * clonedItemsCount - itemWidth / 2;
    const nearEnd = scrollOffset > itemWidth * (extendedItems.length - clonedItemsCount - 1) + itemWidth / 2;
    if (nearEnd) {
      container.style.scrollBehavior = 'auto';
      container.scrollLeft = isRTL ? -itemWidth * clonedItemsCount : itemWidth * clonedItemsCount;
      container.style.scrollBehavior = 'smooth';
      setActiveIndex(0);
      setActiveExtendedIndex(clonedItemsCount);
    } else if (nearStart) {
      container.style.scrollBehavior = 'auto';
      container.scrollLeft = isRTL ? -itemWidth * (items.length + clonedItemsCount - 1) : itemWidth * (items.length + clonedItemsCount - 1);
      container.style.scrollBehavior = 'smooth';
      setActiveIndex(items.length - 1);
      setActiveExtendedIndex(items.length + clonedItemsCount - 1);
    } else {
      setActiveIndex((currentIndex - clonedItemsCount + items.length) % items.length);
      setActiveExtendedIndex(currentIndex);
    }
  }, [items?.length, clonedItemsCount, extendedItems.length, isRTL]);
  (0, _react.useLayoutEffect)(() => {
    if (!containerRef.current) return;
    updateItemWidth();
    const itemWidth = itemWidthRef.current;
    const totalOffset = itemWidth * clonedItemsCount;
    const originalScrollBehavior = containerRef.current.style.scrollBehavior;
    containerRef.current.style.scrollBehavior = 'auto';
    containerRef.current.scrollLeft = isRTL ? -totalOffset : totalOffset;
    containerRef.current.style.scrollBehavior = originalScrollBehavior || 'smooth';
    setActiveIndex(0);
    setActiveExtendedIndex(clonedItemsCount);
  }, [items?.length, clonedItemsCount, isRTL, updateItemWidth]);
  const containerWidth = (0, _base.useWidthObserver)(containerRef);
  (0, _react.useEffect)(() => {
    if (containerWidth > 0) {
      updateItemWidth();
      resetPosition();
    }
  }, [containerWidth, updateItemWidth, resetPosition]);
  (0, _react.useEffect)(() => {
    const container = containerRef.current;
    if (!container) return;
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(resetPosition, 50);
    };
    container.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [resetPosition]);
  const scrollTo = (0, _react.useCallback)(direction => {
    if (!containerRef.current || isScrollingRef.current) return;
    isScrollingRef.current = true;
    const container = containerRef.current;
    const itemWidth = itemWidthRef.current;
    const currentScroll = isRTL ? -container.scrollLeft : container.scrollLeft;
    const scrollAmount = direction === 'next' ? itemWidth : -itemWidth;
    container.scrollTo({
      left: isRTL ? -(currentScroll + scrollAmount) : currentScroll + scrollAmount,
      behavior: 'smooth'
    });
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 750);
  }, [isRTL]);
  const handleNext = (0, _react.useCallback)(() => scrollTo('next'), [scrollTo]);
  const handlePrev = (0, _react.useCallback)(() => scrollTo('prev'), [scrollTo]);
  return {
    containerRef,
    extendedItems,
    activeIndex,
    activeExtendedIndex,
    handleNext,
    handlePrev
  };
}